import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeBackdrop } from '../lib/themeEmblem';
import { useTheme } from '../lib/useTheme';

/**
 * /pricing — v2 REFACTOR per spec §5.
 *
 * Editorial 風格（不是 SaaS 三欄方案表）：
 *   - Hero kicker + serif title
 *   - Setup section（兩個方案，但用「prose card」呈現，不是價格塔）
 *   - Monthly section（三方案橫排但走 typography-heavy，不是 feature 表）
 *   - 「餐廳比喻」FAQ 用大字引文（pull-quote）呈現
 *   - 其餘 FAQ 維持 details/summary
 *   - Trust 條目改成 inline pills，不要佔用 grid 空間
 */

type SetupKey = 'single' | 'full';
const SETUP_TIERS: SetupKey[] = ['single', 'full'];

type MonthlyKey = 'starter' | 'growth' | 'pro';
const MONTHLY_TIERS: MonthlyKey[] = ['starter', 'growth', 'pro'];

export default function Pricing() {
  const { t } = useTranslation();
  const theme = useTheme();
  const whyItems = t('pricing.why.items', { returnObjects: true }) as string[];
  const trustItems = t('pricing.trust.items', { returnObjects: true }) as string[];
  const faqItems = t('pricing.faq.items', { returnObjects: true }) as Array<{
    q: string;
    a: string;
    pullQuote?: boolean;
  }>;

  return (
    <main className="relative isolate min-h-screen">
      <ThemeBackdrop theme={theme} />

      <article className="relative z-10 mx-auto w-full max-w-3xl px-6 pb-24 pt-24 sm:pt-28">
        <Link to="/" className="mb-10 inline-block text-sm text-muted hover:text-fg">
          ← {t('common.nav.back')}
        </Link>

        {/* Hero — editorial */}
        <header className="mb-20">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('pricing.kicker')}
          </p>
          <h1
            className="text-balance font-display text-4xl leading-[1.05] text-heading sm:text-5xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('pricing.hero.title')}
          </h1>
          <p className="mt-5 max-w-xl text-pretty text-base leading-relaxed text-muted sm:text-lg">
            {t('pricing.hero.subtitle')}
          </p>
        </header>

        {/* Setup section */}
        <section className="mb-20">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('pricing.setup.kicker')}
          </p>
          <h2
            className="font-display text-3xl leading-tight text-heading sm:text-4xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('pricing.setup.heading')}
          </h2>
          <p className="mt-3 max-w-xl text-pretty text-base text-muted">
            {t('pricing.setup.intro')}
          </p>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {SETUP_TIERS.map((key) => {
              const items = t(`pricing.setup.${key}.items`, {
                returnObjects: true,
              }) as string[];
              return (
                <div
                  key={key}
                  className="flex flex-col border border-border bg-surface p-7"
                  style={{ borderRadius: 'var(--radius-card)' }}
                >
                  <h3 className="font-display text-2xl text-heading">
                    {t(`pricing.setup.${key}.title`)}
                  </h3>
                  <p className="mt-2 font-display text-3xl text-accent">
                    {t(`pricing.setup.${key}.price`)}
                  </p>
                  <ul className="mt-6 space-y-3 text-sm leading-relaxed text-muted">
                    {items.map((item, i) => (
                      <li key={i} className="flex gap-3">
                        <span
                          aria-hidden
                          className="mt-1 h-1 w-1 shrink-0 rounded-full"
                          style={{ background: 'var(--color-accent)' }}
                        />
                        <span className="text-pretty">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* Monthly section */}
        <section className="mb-20">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('pricing.monthly.kicker')}
          </p>
          <h2
            className="font-display text-3xl leading-tight text-heading sm:text-4xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('pricing.monthly.heading')}
          </h2>
          <p className="mt-3 max-w-xl text-pretty text-base text-muted">
            {t('pricing.monthly.intro')}
          </p>

          <div className="mt-10 grid gap-px overflow-hidden border border-border md:grid-cols-3"
            style={{ borderRadius: 'var(--radius-card)', background: 'var(--color-border)' }}
          >
            {MONTHLY_TIERS.map((key) => {
              const isFeatured = key === 'growth';
              const badge = isFeatured ? t('pricing.monthly.growth.badge') : null;
              return (
                <div
                  key={key}
                  className="relative flex flex-col gap-4 bg-surface p-7"
                >
                  {badge && (
                    <span
                      className="absolute right-4 top-4 px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-[0.18em]"
                      style={{
                        background: 'var(--color-accent)',
                        color: 'var(--color-accent-ink)',
                        borderRadius: 'var(--radius-pill)',
                      }}
                    >
                      {badge}
                    </span>
                  )}
                  <h3 className="font-display text-2xl text-heading">
                    {t(`pricing.monthly.${key}.name`)}
                  </h3>
                  <p className="font-display text-2xl text-accent">
                    {t(`pricing.monthly.${key}.price`)}
                  </p>
                  <p className="text-sm leading-relaxed text-fg">
                    {t(`pricing.monthly.${key}.included`)}
                  </p>
                  <p className="mt-auto font-mono text-[0.65rem] uppercase tracking-[0.18em] text-muted">
                    {t(`pricing.monthly.${key}.overage`)}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Why */}
        <section className="mb-20">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('pricing.why.kicker')}
          </p>
          <h2
            className="font-display text-3xl leading-tight text-heading sm:text-4xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('pricing.why.heading')}
          </h2>
          <ul className="mt-8 grid gap-3 text-base leading-relaxed text-fg sm:grid-cols-2">
            {whyItems.map((item, i) => (
              <li key={i} className="flex gap-3">
                <span
                  aria-hidden
                  className="mt-2 h-1 w-1 shrink-0 rounded-full"
                  style={{ background: 'var(--color-accent)' }}
                />
                <span className="text-pretty">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Trust pills */}
        <section className="mb-20">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('pricing.trust.kicker')}
          </p>
          <h2
            className="font-display text-3xl leading-tight text-heading sm:text-4xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('pricing.trust.heading')}
          </h2>
          <ul className="mt-6 flex flex-wrap gap-2">
            {trustItems.map((item, i) => (
              <li
                key={i}
                className="px-4 py-1.5 text-sm text-muted"
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-pill)',
                }}
              >
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ — restaurant-metaphor item gets pull-quote treatment */}
        <section className="mb-16">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('pricing.faq.kicker')}
          </p>
          <h2
            className="font-display text-3xl leading-tight text-heading sm:text-4xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('pricing.faq.heading')}
          </h2>

          <div className="mt-10 space-y-6">
            {faqItems.map((item, i) =>
              item.pullQuote ? (
                <PullQuote key={i} q={item.q} a={item.a} />
              ) : (
                <details
                  key={i}
                  className="group border border-border bg-surface p-5"
                  style={{ borderRadius: 'var(--radius-card)' }}
                >
                  <summary className="cursor-pointer list-none font-display text-lg text-heading marker:hidden">
                    {item.q}
                  </summary>
                  <p className="mt-3 text-pretty text-sm leading-relaxed text-muted">
                    {item.a}
                  </p>
                </details>
              ),
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-border pt-10">
          <p className="text-pretty text-base leading-relaxed text-muted">
            {t('pricing.cta.body')}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="mailto:wtxg@hourjungle.com?subject=Tarot%20Chat%20demo"
              className="px-6 py-3 text-sm font-medium transition-opacity hover:opacity-90"
              style={{
                background: 'var(--btn-bg)',
                color: 'var(--btn-ink)',
                border: '1px solid var(--btn-border, transparent)',
                borderRadius: 'var(--radius-pill)',
                boxShadow: 'var(--btn-shadow)',
              }}
            >
              {t('common.cta.bookCall')}
            </a>
            <Link
              to="/data-residency"
              className="rounded-pill border border-border px-6 py-3 text-sm transition-colors hover:bg-elevated"
            >
              {t('common.nav.dataResidency')}
            </Link>
          </div>
        </section>
      </article>
    </main>
  );
}

/**
 * 餐廳比喻：用大字引文呈現，不藏在 details 裡 — spec §5 點名。
 */
function PullQuote({ q, a }: { q: string; a: string }) {
  return (
    <figure
      className="my-10 border-l-2 px-6 py-2 sm:px-10"
      style={{ borderColor: 'var(--color-accent)' }}
    >
      <blockquote
        className="text-pretty font-display text-2xl leading-snug text-heading sm:text-3xl"
        style={{ letterSpacing: 'var(--h1-tracking)' }}
      >
        “{a}”
      </blockquote>
      <figcaption className="mt-4 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted">
        — {q}
      </figcaption>
    </figure>
  );
}
